import { Component, signal, computed, Input, OnInit, inject, output } from "@angular/core";
import { AgentParam, ITag } from "../../models";
import { AppConfigService } from "../../services";

@Component({
  selector: "app-conversation-tags",
  templateUrl: "./conversation-tags.component.html",
  styleUrl: "./conversation-tags.component.scss",
  standalone: false,
})
export class ConversationTagsComponent implements OnInit {
  @Input()
  set activeTags(tags: string[]) {
    this.selectedTags.set(tags.map((t) => ({ label: t, value: t })));
  }

  readonly dismiss = output();
  readonly changed = output<string[]>();

  tags = signal<ITag[]>([]);
  selectedTags = signal<ITag[]>([]);
  tagQuery = signal<string>("");
  tagQueryResults = computed(() =>
    this.tagQuery().trim().length === 0
      ? this.tags()
      : this.tags().filter((f) =>
          f.value.toLowerCase().includes(this.tagQuery().trim().toLowerCase())
        )
  );
  isTagQueryResultsEmpty = computed(() => this.tagQueryResults().length === 0);
  isCustomTagAvailable = signal(false);
  isTagAvailable = computed(() => this.tags().length > 0);

  constructor() {
    const config = inject(AppConfigService);

    if (config.agentParam(AgentParam.INTERACTION_TAGS)?.length > 0) {
      this.tags.set(
        [...config.agentParam(AgentParam.INTERACTION_TAGS)]
          .sort()
          .map((t) => ({ value: t, label: t }))
      );
    }
    this.isCustomTagAvailable.set(
      config.agentParam(AgentParam.INTERACTION_CUSTOM_TAGGING_ENABLED)
    );
  }

  ngOnInit(): void {
    if (this.selectedTags().length > 0) {
      // check all the selected tags and try to find custom ones
      this.selectedTags().forEach((s) => {
        // if it's a custom tag that does not exist in predefined tags, add it
        if (!this.tags().some((t) => t.value === s.value)) {
          this.tags.update((tags) => [...tags, s]);
        }
      });
    }
  }

  close() {
    this.dismiss.emit();
  }

  remove(t: ITag) {
    this.selectedTags.update((s) => s.filter((f) => f.value !== t.value));
    this.notifyChange();
  }

  add(t: ITag) {
    this.selectedTags.update((s) => [...s, t]);
    this.notifyChange();
  }

  create(tagName: string) {
    const name = tagName.trim();
    if (!name) return;
    const tag = { value: name, label: name };
    this.tags.update((t) => [...t, tag]);
    this.add(tag);
    this.tagQuery.set("");
  }

  notifyChange() {
    this.changed.emit(this.selectedTags().map((s) => s.value));
  }

  isTagActive(t: ITag) {
    return this.selectedTags().some((s) => s.value === t.value);
  }
}
